<template>
    <div class="admin-content">
        <LoadingPlaceholder v-if="loading" />
        <div class="row" v-if="!loading">
            <div class="col-sm-4 offset-sm-4">
                <div v-show="saveErrors.length > 0" class="validation-summary-errors">
                    <ul>
                        <li v-for="error in saveErrors">{{ error }}</li>
                    </ul>
                </div>
                <a @click.prevent="changePage({ page: 'AdminTenantManagementIndex' })" :href="indexUrl" class="back-link icon icon-arrow-left">Tenants</a>
                <h1 class="admin-content-heading" id="CreateTenantHeading">New</h1>
                <div role="main" aria-labelledby="CreateTenantHeading">
                    <div>
                        <div class="form-group">
                            <label for="urlSlug">Name in URL</label>
                            <input type="text" class="form-control" id="urlSlug" v-model="urlSlug" />
                            <!--
                            @Html.ValidationMessageFor(i => i.UrlSlug)
                            -->
                        </div>
                    </div>
                    <div>
                        <div class="form-group">
                            <label for="type">Type</label>
                            <select class="form-control" id="type" v-model="type">
                                <option :value="null">-- Select a type --</option>
                                <option :value="item" v-for="item in typeOptions">{{ item }}</option>
                            </select>
                            <!--
                            @Html.ValidationMessageFor(i => i.Type)
                            -->
                        </div>
                    </div>
                    <div>
                        <div class="form-group">
                            <label for="displayLabel">Display Label</label>
                            <input type="text" class="form-control" id="displayLabel" v-model="displayLabel" />
                            <!--
                            @Html.ValidationMessageFor(i => i.DisplayLabel)
                            -->
                        </div>
                    </div>
                    <div>
                        <div class="form-group">
                            <label for="description">Description</label>
                            <input type="text" class="form-control" id="description" v-model="description" />
                            <!--
                            @Html.ValidationMessageFor(i => i.Description)
                            -->
                        </div>
                    </div>
                    <div>
                        <div class="form-group">
                            <label for="tier">Tier</label>
                            <select class="form-control" id="tier" v-model="tier">
                                <option :value="null">-- Select a tier --</option>
                                <option :value="item" v-for="item in tierOptions">{{ item }}</option>
                            </select>
                            <!--
                            @Html.ValidationMessageFor(i => i.Tier)
                            -->
                        </div>
                    </div>
                    <div>
                        <div class="form-group">
                            <label for="parentCode">Parent</label>
                            <select class="form-control" id="parentCode" v-model="parentCode">
                                <option :value="null">-- None --</option>
                                <option :value="item.code" v-for="item in existingTenants">{{ item.label }}</option>
                            </select>
                            <!--
                            @Html.ValidationMessageFor(i => i.Code)
                            -->
                        </div>
                    </div>
                    <div class="col-sm-12">
                        <br />
                        <div class="text-center save-section">
                            <input type="button" value="Create" class="btn btn-primary" @click.prevent="submit" />
                            <a @click.prevent="changePage({ page: 'AdminTenantManagementIndex' })" :href="indexUrl" class="btn btn-bare">Cancel</a>
                            <span v-show="saveStatus == 'ok'" class="icon icon-check"></span>
                            <span v-show="saveStatus == 'fail'" class="icon icon-attention"></span>
                            <LoadingWheel :width="16" v-show="saveStatus == 'in-progress'" />
                        </div>
                    </div>
                </div>
                <br />
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, onActivated, SetupContext, inject } from "vue";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import LoadingPlaceholder from "@/lib/LoadingPlaceholder.3.vue";
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { getXsftHeader } from "@/lib/getXsrfToken.3";
import getData from "../../lib/getData";
import { useMainStore } from "@/mainStore";
import { asyncComputed } from "@vueuse/core";
import { NewTenantViewModel } from "@cs/NewTenantViewModel";
import { changePageInjectKey } from "@/AppShell.3.vue.InjectKeys";

export default defineComponent({
    components: {
        LoadingWheel,
        LoadingPlaceholder,
    },
    setup(_, { emit }: SetupContext) {
        const mainStore = useMainStore();

        const loading = ref(true);
        const code = ref<string>(null!);
        const type = ref<string>(null!);
        const urlSlug = ref<string>(null!);
        const displayLabel = ref<string>(null!);
        const description = ref<string>(null!);
        const tier = ref<string>(null!);
        const parentCode = ref<string>(null!);
        const existingTenants = ref<{ label: string; code: string }[]>([]);
        const tierOptions = ref<string[]>([]);
        const typeOptions = ref<string[]>([]);
        const saveStatus = ref<string>(null!);
        const saveErrors = ref<string[]>([]);

        const indexUrl = asyncComputed(() => {
            return mainStore.getUrl("adminTenantManagementIndex");
        });

        const submit = async () => {
            saveStatus.value = "in-progress";
            var saveUrl = await mainStore.getUrl("adminTenantManagementNewSave");
            const formData = new FormData(); // TODO json?
            function appendFormData(key: string, value: string) {
                if (value != null) {
                    formData.append(key, value);
                }
            }
            appendFormData("UrlSlug", urlSlug.value || "");
            appendFormData("Type", type.value || "");
            appendFormData("DisplayLabel", displayLabel.value);
            appendFormData("ParentCode", parentCode.value);
            appendFormData("Description", description.value);
            appendFormData("Tier", tier.value);
            try {
                const saveResponse = await fetch(saveUrl, {
                    method: "POST",
                    headers: await getXsftHeader(mainStore),
                    credentials: "same-origin",
                    body: formData,
                });
                throwIfNonSuccessStatus(saveResponse);
                const saveJson = await saveResponse.json();
                if (saveJson.Success) {
                    saveStatus.value = "ok";
                    emit("change-page", { page: "AdminTenantManagementEdit", settings: { code: saveJson.Id } });
                } else {
                    saveStatus.value = "fail";
                    saveErrors.value = saveJson.Errors || ["Unknown error occurred"];
                }
            } catch {
                saveStatus.value = "fail";
                saveErrors.value = ["Unknown error occurred"];
            }
        };

        const _onActivated = async () => {
            loading.value = true;
            const tenantNewData: NewTenantViewModel = await getData(mainStore, "AdminTenantManagementNew", () => mainStore.getUrl("adminTenantManagementNewData"));
            tierOptions.value = tenantNewData.TierOptions!;
            typeOptions.value = tenantNewData.TypeOptions!;
            var comparer = new Intl.Collator([], { sensitivity: "base" });
            existingTenants.value = Object.keys(tenantNewData.TenantOptions!)
                .map(k => ({ label: tenantNewData.TenantOptions![k], code: k }))
                .sort((a, b) => comparer.compare(a.label, b.label));
            loading.value = false;
        };
        onActivated(_onActivated);

        const expose = {
            // States
            loading,
            code,
            type,
            urlSlug,
            displayLabel,
            description,
            tier,
            parentCode,
            existingTenants,
            tierOptions,
            typeOptions,
            saveStatus,
            saveErrors,

            // Async Computed
            indexUrl,

            // Methods
            submit,

            changePage: inject(changePageInjectKey)!,
        };

        ("#if JEST");
        //@ts-ignore
        expose.onActivated = _onActivated;
        //@ts-ignore
        ("#endif");

        return expose;
    },
});
</script>
